// frontend/TwitterAnalytics/modules/DataAnalytics.jsx
/**
 * Module 5: Comprehensive Data Analytics
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Paper, Typography, Button, Select, MenuItem,
  FormControl, InputLabel, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Card, CardContent,
  Dialog, DialogTitle, DialogContent, CircularProgress, Tooltip, 
  ToggleButton, ToggleButtonGroup
} from '@mui/material';
import {
  Download as DownloadIcon,
  Compare as CompareIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, 
  ResponsiveContainer, AreaChart, Area, Legend, Cell
} from 'recharts';
import axiosInstance from "../../../utils/axiosInstance";

// Engagement Funnel Chart
const EngagementFunnel = ({ data }) => {
  if (!data?.funnel) return null;
  
  const colors = ['#8884d8', '#83a6ed', '#8dd1e1', '#82ca9d', '#a4de6c', '#d0ed57'];
  
  return (
    <Box>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data.funnel} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          <YAxis type="category" dataKey="stage" width={100} />
          <RechartsTooltip 
            formatter={(value, name) => [value.toLocaleString(), name]}
          />
          <Bar dataKey="value" fill="#8884d8">
            {data.funnel.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={6} md={2.4}>
          <Typography variant="body2" color="textSecondary">Views → Likes</Typography>
          <Typography variant="h6">{data.conversion_rates?.views_to_likes}%</Typography>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Typography variant="body2" color="textSecondary">Views → Comments</Typography>
          <Typography variant="h6">{data.conversion_rates?.views_to_comments}%</Typography>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Typography variant="body2" color="textSecondary">Likes → Comments</Typography>
          <Typography variant="h6">{data.conversion_rates?.likes_to_comments}%</Typography>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Typography variant="body2" color="textSecondary">Comments → RTs</Typography>
          <Typography variant="h6">{data.conversion_rates?.comments_to_retweets}%</Typography>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Typography variant="body2" color="textSecondary">Overall Rate</Typography>
          <Typography variant="h6" color="primary">{data.conversion_rates?.overall_engagement}%</Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

// Time Heatmap Component
const TimeHeatmap = ({ data }) => {
  if (!data?.heatmap) return null;
  
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const hours = Array.from({ length: 24 }, (_, i) => i);
  
  const getColor = (value, max) => {
    if (value === 0) return '#f5f5f5';
    const intensity = Math.min(value / max, 1);
    return `rgba(25, 118, 210, ${0.2 + intensity * 0.8})`;
  };
  
  const maxComments = Math.max(...data.heatmap.map(d => d.comments), 1);
  
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ display: 'flex', minWidth: 700 }}>
        <Box sx={{ width: 50 }} />
        {hours.map(h => (
          <Box key={h} sx={{ width: 25, textAlign: 'center' }}>
            <Typography variant="caption">{h}</Typography>
          </Box>
        ))}
      </Box>
      {days.map((day, dayIdx) => (
        <Box key={day} sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ width: 50 }}>
            <Typography variant="caption">{day}</Typography>
          </Box>
          {hours.map(hour => {
            const cell = data.heatmap.find(d => d.day_index === dayIdx && d.hour === hour);
            return (
              <Tooltip
                key={`${dayIdx}-${hour}`}
                title={`${day} ${hour}:00 - ${cell?.comments || 0} comments`}
              >
                <Box
                  sx={{
                    width: 25,
                    height: 20,
                    bgcolor: getColor(cell?.comments || 0, maxComments),
                    border: '1px solid #fff',
                    cursor: 'pointer'
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>
      ))}
      
      {data.insights && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Insights</Typography>
          <Grid container spacing={2}>
            {data.insights.best_posting_time && (
              <Grid item xs={4}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1, px: 2 }}>
                    <Typography variant="caption" color="success.main">Best Time to Post</Typography>
                    <Typography variant="body2">
                      {data.insights.best_posting_time.day} {data.insights.best_posting_time.hour}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
            {data.insights.most_active_time && (
              <Grid item xs={4}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1, px: 2 }}>
                    <Typography variant="caption" color="primary.main">Most Active</Typography>
                    <Typography variant="body2">
                      {data.insights.most_active_time.day} {data.insights.most_active_time.hour}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
            {data.insights.most_negative_time && (
              <Grid item xs={4}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1, px: 2 }}>
                    <Typography variant="caption" color="error.main">Most Negative</Typography>
                    <Typography variant="body2">
                      {data.insights.most_negative_time.day} {data.insights.most_negative_time.hour}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

// Trend Chart Component
const TrendChart = ({ data, metric }) => {
  if (!data?.trend) return null;
  
  return (
    <ResponsiveContainer width="100%" height={300}>
      <AreaChart data={data.trend}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="date" tickFormatter={(d) => format(new Date(d), 'MMM d')} />
        <YAxis />
        <RechartsTooltip labelFormatter={(d) => format(new Date(d), 'MMM d, yyyy')} />
        <Legend />
        {metric === 'engagement' && (
          <>
            <Area type="monotone" dataKey="likes" stackId="1" stroke="#82ca9d" fill="#82ca9d" name="Likes" />
            <Area type="monotone" dataKey="retweets" stackId="1" stroke="#8884d8" fill="#8884d8" name="Retweets" />
            <Area type="monotone" dataKey="replies" stackId="1" stroke="#ffc658" fill="#ffc658" name="Replies" />
          </>
        )}
        {metric === 'sentiment' && (
          <>
            <Area type="monotone" dataKey="positive_comments" stackId="1" stroke="#4caf50" fill="#4caf50" name="Positive" />
            <Area type="monotone" dataKey="neutral_comments" stackId="1" stroke="#9e9e9e" fill="#9e9e9e" name="Neutral" />
            <Area type="monotone" dataKey="negative_comments" stackId="1" stroke="#f44336" fill="#f44336" name="Negative" />
          </>
        )}
        {metric === 'posts' && (
          <Area type="monotone" dataKey="posts" stroke="#1976d2" fill="#1976d2" name="Posts" />
        )}
      </AreaChart>
    </ResponsiveContainer>
  );
};

const DataAnalytics = ({ apiTier, onShowSnackbar }) => {
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('30d');
  
  const [summary, setSummary] = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  const [timeData, setTimeData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [trendMetric, setTrendMetric] = useState('engagement');
  
  // Post comparison
  const [posts, setPosts] = useState([]);
  const [comparisonDialog, setComparisonDialog] = useState(false);
  const [postAId, setPostAId] = useState('');
  const [postBId, setPostBId] = useState('');
  const [comparisonResult, setComparisonResult] = useState(null);
  const [comparing, setComparing] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, [period]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [summaryRes, funnelRes, timeRes, trendRes, postsRes] = await Promise.all([
        axiosInstance.get('/api/twitter/analytics/summary', { params: { period } }),
        axiosInstance.get('/api/twitter/analytics/funnel'),
        axiosInstance.get('/api/twitter/analytics/time'),
        axiosInstance.get('/api/twitter/analytics/trends', { params: { period, granularity: 'daily' } }),
        axiosInstance.get('/api/twitter/posts', { params: { per_page: 50 } })
      ]);
      
      if (summaryRes.data.success) setSummary(summaryRes.data.data);
      if (funnelRes.data.success) setFunnelData(funnelRes.data.data);
      if (timeRes.data.success) setTimeData(timeRes.data.data);
      if (trendRes.data.success) setTrendData(trendRes.data.data);
      if (postsRes.data.success) setPosts(postsRes.data.data);
    } catch (err) {
      onShowSnackbar('Failed to fetch analytics', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCompare = async () => {
    if (!postAId || !postBId) {
      onShowSnackbar('Select two posts to compare', 'warning');
      return;
    }
    setComparing(true);
    try {
      const res = await axiosInstance.get('/api/twitter/analytics/compare', {
        params: { post_a_id: postAId, post_b_id: postBId }
      });
      const response = res.data;
      if (response.success) {
        setComparisonResult(response.data);
      }
    } catch (err) {
      onShowSnackbar('Comparison failed', 'error');
    } finally {
      setComparing(false);
    }
  };

  const handleExport = async (type) => {
    try {
      onShowSnackbar(`Exporting ${type}...`, 'success');
      
      const response = await axiosInstance.get('/api/twitter/export', {
        params: { type },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `twitter_${type}_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      onShowSnackbar('Export failed', 'error');
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  if (loading && !summary) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {/* Period Selector & Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box display="flex" gap={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Period</InputLabel>
              <Select value={period} label="Period" onChange={(e) => setPeriod(e.target.value)}>
                <MenuItem value="7d">Last 7 Days</MenuItem>
                <MenuItem value="30d">Last 30 Days</MenuItem>
                <MenuItem value="90d">Last 90 Days</MenuItem>
                <MenuItem value="all">All Time</MenuItem>
              </Select>
            </FormControl>
            <Button startIcon={<RefreshIcon />} onClick={fetchAllData}>Refresh</Button>
          </Box>
          
          <Box display="flex" gap={1}>
            <Button variant="outlined" startIcon={<CompareIcon />} onClick={() => setComparisonDialog(true)}>
              Compare Posts
            </Button>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => handleExport('posts')}>
              Export Posts
            </Button>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => handleExport('comments')}>
              Export Comments
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={2}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">{summary.posts?.total || 0}</Typography>
              <Typography variant="body2" color="textSecondary">Posts</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={2}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">{formatNumber(summary.posts?.impressions)}</Typography>
              <Typography variant="body2" color="textSecondary">Impressions</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={2}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">{formatNumber(summary.posts?.likes)}</Typography>
              <Typography variant="body2" color="textSecondary">Likes</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={2}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">{summary.users?.total_interactors || 0}</Typography>
              <Typography variant="body2" color="textSecondary">Users</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={2}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
              <Typography variant="h4">{summary.sentiment?.positive || 0}</Typography>
              <Typography variant="body2">Positive</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={2}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light' }}>
              <Typography variant="h4">{summary.sentiment?.negative || 0}</Typography>
              <Typography variant="body2">Negative</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Grid container spacing={3}>
        {/* Engagement Funnel */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Engagement Funnel</Typography>
            <EngagementFunnel data={funnelData} />
          </Paper>
        </Grid>

        {/* Time Heatmap */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Activity by Time</Typography>
            <TimeHeatmap data={timeData} />
          </Paper>
        </Grid>

        {/* Trend Chart */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Trends Over Time</Typography>
              <ToggleButtonGroup
                value={trendMetric}
                exclusive
                onChange={(e, v) => v && setTrendMetric(v)}
                size="small"
              >
                <ToggleButton value="engagement">Engagement</ToggleButton>
                <ToggleButton value="sentiment">Sentiment</ToggleButton>
                <ToggleButton value="posts">Posts</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <TrendChart data={trendData} metric={trendMetric} />
          </Paper>
        </Grid>
      </Grid>

      {/* Export Options */}
      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6" gutterBottom>Export Data</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} md={2}>
            <Button fullWidth variant="outlined" onClick={() => handleExport('posts')}>Posts CSV</Button>
          </Grid>
          <Grid item xs={6} md={2}>
            <Button fullWidth variant="outlined" onClick={() => handleExport('comments')}>Comments CSV</Button>
          </Grid>
          <Grid item xs={6} md={2}>
            <Button fullWidth variant="outlined" onClick={() => handleExport('users')}>Users CSV</Button>
          </Grid>
          <Grid item xs={6} md={2}>
            <Button fullWidth variant="outlined" onClick={() => handleExport('negative_comments')}>Negative CSV</Button>
          </Grid>
          <Grid item xs={6} md={2}>
            <Button fullWidth variant="outlined" onClick={() => handleExport('issues')}>Issues CSV</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Comparison Dialog */}
      <Dialog open={comparisonDialog} onClose={() => setComparisonDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Compare Posts</Typography>
            <IconButton onClick={() => setComparisonDialog(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Post A</InputLabel>
                <Select value={postAId} label="Post A" onChange={(e) => setPostAId(e.target.value)}>
                  {posts.map(p => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.text?.substring(0, 50)}...
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Post B</InputLabel>
                <Select value={postBId} label="Post B" onChange={(e) => setPostBId(e.target.value)}>
                  {posts.map(p => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.text?.substring(0, 50)}...
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          <Button 
            variant="contained" 
            onClick={handleCompare} 
            disabled={comparing}
            startIcon={comparing ? <CircularProgress size={20} /> : <CompareIcon />}
          >
            Compare
          </Button>

          {comparisonResult && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Winner: <Chip label={`Post ${comparisonResult.winner === 'post_a' ? 'A' : 'B'}`} color="success" />
              </Typography>
              
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Metric</TableCell>
                      <TableCell align="center">Post A</TableCell>
                      <TableCell align="center">Post B</TableCell>
                      <TableCell align="center">Difference</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {['impressions', 'likes', 'retweets', 'replies', 'engagement_score'].map(metric => (
                      <TableRow key={metric}>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{metric.replace('_', ' ')}</TableCell>
                        <TableCell align="center">
                          {formatNumber(comparisonResult.post_a?.metrics?.[metric])}
                        </TableCell>
                        <TableCell align="center">
                          {formatNumber(comparisonResult.post_b?.metrics?.[metric])}
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={`${comparisonResult.differences?.[metric]?.percentage > 0 ? '+' : ''}${comparisonResult.differences?.[metric]?.percentage}%`}
                            size="small"
                            color={comparisonResult.differences?.[metric]?.percentage > 0 ? 'success' : 'error'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DataAnalytics;